import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { v, num, Checkbox, FieldRow, KeepTogether, PageBreak, Section, SectionNumberingProvider, SigningBlock, TemplateDocument, TemplateFooter, TemplateLetterhead, } from '../../../atoms/index.js';
import { expandPartySlot, COTENANT_SUFFIX } from '../../../parties/index.js';
/**
 * The 18 d Godkännande is a tenant consent form — the landlord depends
 * on the tenant actually signing it. In-flight follow-up reminders are
 * no longer declared per-template: the materializer injects the default
 * due-date-anchored signing monitor (`SIGNING_MONITOR_RULE`) into every
 * Scrive send, so this form gets deadline follow-up like any other
 * contract without a bespoke rule here.
 */
export const metadata = {
    // Document sections (per ADR-0049). §1–3 render in the HeaderTables
    // helper, §4–7 in the Body; the provider on the Body covers both
    // because section numbering flows through React context.
    sections: [
        { ref: 'hyresvard', title: 'Uppgifter om hyresvärden' },
        { ref: 'hyresobjekt', title: 'Uppgifter om hyresobjekt' },
        { ref: 'hyresgast', title: 'Uppgifter om hyresgästen' },
        { ref: 'byggbeskrivning', title: 'Byggbeskrivning' },
        { ref: 'bilagor', title: 'Bilagor' },
        { ref: 'godkannande', title: 'Hyresgästens godkännande' },
        { ref: 'underskrift', title: 'Underskrift' },
    ],
    inputSections: [
        { id: 'godkannande', label: 'Byggbeskrivning & godkännande', sectionRefs: ['byggbeskrivning', 'bilagor', 'godkannande'] },
    ],
    templateKey: 'godkannande_18d',
    version: 'v1',
    category: 'Ombyggnad och ändring',
    title: 'Godkännande av ombyggnads- och ändringsåtgärder enligt 12 kap. 18 d § jordabalken',
    shortTitle: 'Godkännande 18 d §',
    titleSuffix: { key: 'apartment_official_number', prefix: 'lgh' },
    formNumber: 'Formulär R-07',
    preamble: 'Hyresvärden inhämtar härmed hyresgästens godkännande av planerade ombyggnads- och ändringsåtgärder.',
    description: 'Hyresgästens godkännande av ombyggnads- och ändringsåtgärder enligt 12 kap. 18 d § jordabalken.\n\n' +
        'Vem undertecknar: hyresgästen (och eventuell medhyresgäst). Hyresvärden visas i sidhuvudet men signerar inte.\n' +
        'Leverans: skickas via Scrive — eget signerbart dokument.\n\n' +
        'Två sidor: sida 1 är hyresvärdens byggbeskrivning med valda bilagor; sida 2 är själva överenskommelsen som hyresgästen undertecknar.\n\n' +
        'Kopplad mall — skapas under det 18 e Meddelande som skickas till hyresgästen. Bifogas själva meddelandet vid utskick (rekommenderad post).',
    status: 'active',
    signingMode: 'manual_delivery',
    connectedTo: ['meddelande_18e'],
    // Same renovation case, same parties + lägenhet as the 18 e Meddelande
    // (ADR-0055). Roles line up 1:1 with the parent (lessor0 = hyresvärd,
    // lessee0 = hyresgäst); the parent has no co-tenant slot, so the child's
    // lessee1 is simply not populated and is filled by hand when there are
    // two tenants. `property_address` / `property_designation` come via the
    // inherited property; the three plain apartment scalars are mapped
    // explicitly. No IMPORT_TODO: meddelande_18e is a created-only statutory
    // notice (no scopes.imported fields), so it is never an imported parent.
    inheritFromParent: {
        parties: 'by-role-ordinal',
        property: true,
        values: [
            { key: 'apartment_official_number', fromParentKey: 'apartment_official_number' },
            { key: 'apartment_rooms', fromParentKey: 'apartment_rooms' },
            { key: 'apartment_area_sqm', fromParentKey: 'apartment_area_sqm' },
        ],
    },
    signingPlacementAnchors: 'per-party-label',
    partySlots: [
        {
            id: 'lessor',
            label: 'Hyresvärd',
            role: 'lessor',
            ordinal: 0,
            required: true,
            signs: false,
            help: 'Hyresvärdens uppgifter visas i sidhuvudet — undertecknar inte.',
        },
        {
            id: 'lessee_primary',
            label: 'Hyresgäst (primär)',
            signingLabel: 'Hyresgäst 1',
            role: 'lessee',
            ordinal: 0,
            required: true,
            signs: true,
        },
        {
            id: 'lessee_co',
            label: 'Medhyresgäst',
            signingLabel: 'Eventuell hyresgäst 2',
            role: 'lessee',
            ordinal: 1,
            signs: true,
            help: 'Valfritt — lägg till om kontraktet har två hyresgäster.',
        },
    ],
    // Party projection (ADR-0065) — editor fields; the body renders parties
    // across separate sections, so it is not PartyBlock-driven.
    partyProjection: [
        {
            slot: 'lessor',
            section: 'hyresvard',
            fields: [
                { attr: 'legal_name', key: 'landlord_name', requiredAtFinal: true },
                { attr: 'id_number', key: 'landlord_org_number', requiredAtFinal: true },
                { attr: 'address', key: 'landlord_address', requiredAtFinal: true },
            ],
        },
        {
            slot: 'lessee_primary',
            section: 'hyresgast',
            fields: [
                { attr: 'legal_name', key: 'tenant_name', requiredAtFinal: true },
                { attr: 'id_number', key: 'tenant_personnummer', help: 'PII — ej sökbart' },
                { attr: 'email', key: 'tenant_email' },
                { attr: 'phone', key: 'tenant_phone' },
            ],
        },
        {
            slot: 'lessee_co',
            section: 'hyresgast',
            labelSuffix: COTENANT_SUFFIX,
            fields: [
                { attr: 'legal_name', key: 'cotenant_name' },
                { attr: 'id_number', key: 'cotenant_personnummer', help: 'PII — ej sökbart' },
                { attr: 'email', key: 'cotenant_email' },
                { attr: 'phone', key: 'cotenant_phone' },
            ],
        },
    ],
    propertySlots: [
        {
            id: 'property',
            label: 'Fastighet',
            multi: false,
            required: true,
        },
    ],
};
export const variableSchema = [
    // Hyresvärd — generated from metadata.partyProjection (ADR-0065).
    ...expandPartySlot(metadata, 'lessor'),
    // Hyresobjekt
    {
        key: 'apartment_official_number',
        label: 'Lägenhetsnummer',
        type: 'text',
        section: 'hyresobjekt',
        // Per-unit identifier — never carried over by copy-from (ADR-0050).
        scopes: { copy: 'never' },
    },
    {
        key: 'apartment_rooms',
        label: 'Antal rum och kök',
        type: 'select',
        section: 'hyresobjekt',
        options: [
            { value: '1 rum och kokskåp', label: '1 rum och kokskåp', code: 'r1_kokskap', summary: '1 room with a kitchenette cupboard.' },
            { value: '1 rum och kokvrå', label: '1 rum och kokvrå', code: 'r1_kokvra', summary: '1 room with a kitchen nook.' },
            { value: '1 rum och kök', label: '1 rum och kök', code: 'r1_kok', summary: '1 room with a full kitchen.' },
            { value: '1,5 rum och kök', label: '1,5 rum och kök', code: 'r1_5_kok', summary: '1.5 rooms with a full kitchen.' },
            { value: '2 rum och kokvrå', label: '2 rum och kokvrå', code: 'r2_kokvra', summary: '2 rooms with a kitchen nook.' },
            { value: '2 rum och kök', label: '2 rum och kök', code: 'r2_kok', summary: '2 rooms with a full kitchen.' },
            { value: '2,5 rum och kök', label: '2,5 rum och kök', code: 'r2_5_kok', summary: '2.5 rooms with a full kitchen.' },
            { value: '3 rum och kök', label: '3 rum och kök', code: 'r3_kok', summary: '3 rooms with a full kitchen.' },
            { value: '3,5 rum och kök', label: '3,5 rum och kök', code: 'r3_5_kok', summary: '3.5 rooms with a full kitchen.' },
            { value: '4 rum och kök', label: '4 rum och kök', code: 'r4_kok', summary: '4 rooms with a full kitchen.' },
            { value: '4,5 rum och kök', label: '4,5 rum och kök', code: 'r4_5_kok', summary: '4.5 rooms with a full kitchen.' },
            { value: '5 rum och kök', label: '5 rum och kök', code: 'r5_kok', summary: '5 rooms with a full kitchen.' },
            { value: '6 rum och kök', label: '6 rum och kök', code: 'r6_kok', summary: '6 rooms with a full kitchen.' },
            { value: '7 rum och kök', label: '7 rum och kök', code: 'r7_kok', summary: '7 rooms with a full kitchen.' },
            { value: '8 rum och kök', label: '8 rum och kök', code: 'r8_kok', summary: '8 rooms with a full kitchen.' },
        ],
    },
    {
        key: 'apartment_area_sqm',
        label: 'Yta i m²',
        type: 'number',
        min: 0,
        section: 'hyresobjekt',
    },
    {
        key: 'property_address',
        label: 'Adress (postnummer och kommun)',
        type: 'textarea',
        section: 'hyresobjekt',
        requiredAtFinal: true,
        autofill: { kind: 'property', field: 'address' },
    },
    {
        key: 'property_designation',
        label: 'Fastighetsbeteckning',
        type: 'text',
        section: 'hyresobjekt',
        requiredAtFinal: true,
        autofill: { kind: 'property', field: 'name' },
    },
    // Hyresgäst (primär) + Medhyresgäst — generated from
    // metadata.partyProjection (ADR-0065).
    ...expandPartySlot(metadata, 'lessee_primary'),
    ...expandPartySlot(metadata, 'lessee_co'),
    // Byggbeskrivning
    {
        key: 'works_common_areas',
        label: 'Åtgärder i fastighetens gemensamma delar',
        type: 'textarea',
        section: 'byggbeskrivning',
        help: 'Specificera åtgärderna som ska genomföras i fastighetens gemensamma delar.',
    },
    {
        key: 'works_apartment',
        label: 'Åtgärder i lägenheten',
        type: 'textarea',
        section: 'byggbeskrivning',
        help: 'Specificera åtgärderna som ska genomföras i lägenheten.',
    },
    // Bilagor (sida 1)
    {
        key: 'annex_byggritning',
        label: 'Byggritning bifogas',
        type: 'boolean',
        section: 'bilagor',
    },
    {
        key: 'annex_planritning',
        label: 'Planritning bifogas',
        type: 'boolean',
        section: 'bilagor',
    },
    {
        key: 'annex_other',
        label: 'Annan bilaga bifogas',
        type: 'boolean',
        section: 'bilagor',
    },
    {
        key: 'annex_other_description',
        label: 'Beskrivning av annan bilaga',
        type: 'text',
        section: 'bilagor',
        visibleWhen: { annex_other: true },
    },
    // Överenskommelse (sida 2) — vad hyresgästen godkänner
    {
        key: 'consent_common_areas',
        label: 'Hyresgästen godkänner åtgärder i fastighetens gemensamma delar',
        type: 'boolean',
        section: 'godkannande',
    },
    {
        key: 'consent_apartment',
        label: 'Hyresgästen godkänner åtgärder i lägenheten',
        type: 'boolean',
        section: 'godkannande',
    },
    {
        key: 'annex_evakueringsavtal',
        label: 'Evakueringsavtal bifogas',
        type: 'boolean',
        section: 'godkannande',
        help: 'Valfri bilaga — kryssa i om ett separat evakueringsavtal följer med.',
    },
    // Underskrift
    {
        key: 'signing_city',
        label: 'Ort',
        type: 'text',
        section: 'underskrift',
        requiredAtFinal: true,
        help: 'T.ex. Stockholm',
    },
    {
        key: 'signing_date',
        label: 'Datum',
        type: 'date',
        section: 'underskrift',
        requiredAtFinal: true,
        // Absolute date — per-contract, opt-in on copy (ADR-0050).
        scopes: { copy: 'manual' },
    },
];
export const sampleValues = {
    landlord_name: 'Bostadsägarna Stockholm AB',
    landlord_org_number: '556677-8899',
    landlord_address: 'Sveavägen 100\n113 50 Stockholm',
    apartment_official_number: '1101',
    apartment_rooms: '2 rum och kök',
    apartment_area_sqm: 58,
    property_address: 'Götgatan 35\n116 21 Stockholm',
    property_designation: 'Klockaren 5',
    tenant_name: 'Anna Andersson',
    tenant_personnummer: '19850412-1234',
    tenant_email: 'anna@example.com',
    tenant_phone: '070-123 45 67',
    cotenant_name: 'Bo Andersson',
    cotenant_personnummer: '19830605-2345',
    cotenant_email: 'bo@example.com',
    cotenant_phone: '070-234 56 78',
    works_common_areas: 'Renovering av trapphus inklusive målning och nytt golvmaterial.\nByte av entrédörrens dörrkod till digital lösning.',
    works_apartment: 'Stambyte (kallt- och varmvatten samt avlopp) i kök och badrum.\nByte av kakel och klinker i badrum.',
    annex_byggritning: true,
    annex_planritning: true,
    annex_other: false,
    annex_other_description: '',
    consent_common_areas: true,
    consent_apartment: true,
    annex_evakueringsavtal: true,
    signing_city: 'Stockholm',
    signing_date: '2026-06-22',
};
const cond = (mode, predicate) => mode === 'blank' ? true : predicate;
export function Letterhead() {
    return _jsx(TemplateLetterhead, { title: metadata.title, formNumber: metadata.formNumber });
}
export function PageFooter() {
    return _jsx(TemplateFooter, { formNumber: metadata.formNumber });
}
function HeaderTables({ values, withTenantContact, }) {
    return (_jsxs(_Fragment, { children: [_jsxs(Section, { sectionRef: "hyresvard", title: "Uppgifter om hyresv\u00E4rden", children: [_jsx(FieldRow, { label: "Namn", children: v(values.landlord_name) }), _jsx(FieldRow, { label: "Personnr / Org.nr", children: v(values.landlord_org_number) }), _jsx(FieldRow, { label: "Adress", children: v(values.landlord_address) })] }), _jsxs(Section, { sectionRef: "hyresobjekt", title: "Uppgifter om hyresobjekt", children: [_jsx(FieldRow, { label: "L\u00E4genhetsnummer", children: v(values.apartment_official_number) }), _jsx(FieldRow, { label: "Antal rum och k\u00F6k", children: v(values.apartment_rooms) }), _jsx(FieldRow, { label: "Yta i m\u00B2", children: num(values.apartment_area_sqm) }), _jsx(FieldRow, { label: "Adress (postnummer och kommun)", children: v(values.property_address) }), _jsx(FieldRow, { label: "Fastighetsbeteckning", children: v(values.property_designation) })] }), _jsxs(Section, { sectionRef: "hyresgast", title: "Uppgifter om hyresg\u00E4sten", children: [_jsx(FieldRow, { label: "Namn", children: v(values.tenant_name) }), _jsx(FieldRow, { label: "Personnr / Org.nr", children: v(values.tenant_personnummer) }), withTenantContact && (_jsxs(_Fragment, { children: [_jsx(FieldRow, { label: "E-post", children: v(values.tenant_email) }), _jsx(FieldRow, { label: "Telefonnummer", children: v(values.tenant_phone) })] }))] })] }));
}
export function Body({ values, mode, signatories, }) {
    return (_jsxs(SectionNumberingProvider, { sections: metadata.sections ?? [], values: values, mode: mode, children: [_jsx("p", { className: "mb-4 italic text-stone-600", children: metadata.preamble }), _jsx(HeaderTables, { values: values, withTenantContact: true }), _jsxs(Section, { sectionRef: "byggbeskrivning", title: "Byggbeskrivning", children: [_jsx("p", { className: "mt-1 mb-1 text-[10.5px] leading-relaxed", children: "I fastighetens gemensamma delar ska fastighets\u00E4garen genomf\u00F6ra f\u00F6ljande delar:" }), _jsx("div", { className: "mb-3 whitespace-pre-line border-b border-stone-300 px-1 font-serif text-[10.5px] leading-relaxed", children: v(values.works_common_areas) }), _jsx("p", { className: "mt-1 mb-1 text-[10.5px] leading-relaxed", children: "I l\u00E4genheten ska fastighets\u00E4garen genomf\u00F6ra f\u00F6ljande \u00E5tg\u00E4rder:" }), _jsx("div", { className: "whitespace-pre-line border-b border-stone-300 px-1 font-serif text-[10.5px] leading-relaxed", children: v(values.works_apartment) })] }), _jsxs(Section, { sectionRef: "bilagor", title: "Bilagor", children: [_jsxs("p", { className: "text-[10.5px] leading-relaxed", children: [_jsx(Checkbox, { checked: !!values.annex_byggritning }), "Byggritning"] }), _jsxs("p", { className: "text-[10.5px] leading-relaxed", children: [_jsx(Checkbox, { checked: !!values.annex_planritning }), "Planritning"] }), _jsxs("p", { className: "text-[10.5px] leading-relaxed", children: [_jsx(Checkbox, { checked: !!values.annex_other }), cond(mode, !!values.annex_other_description) ? (_jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.annex_other_description) })) : (_jsx("span", { className: "text-stone-500", children: "Annan bilaga" }))] })] }), _jsx(PageBreak, {}), _jsx("p", { className: "mb-4 mt-2 text-center text-[13px] font-semibold", children: "\u00D6verenskommelse om \u00E4ndringsarbete i bostadsl\u00E4genhet" }), _jsx(HeaderTables, { values: values, withTenantContact: false }), _jsxs(Section, { sectionRef: "godkannande", title: "Hyresg\u00E4stens godk\u00E4nnande", children: [_jsx("p", { className: "mt-1 mb-2 text-[10.5px] leading-relaxed", children: "Hyresg\u00E4sten godk\u00E4nner att hyresv\u00E4rden f\u00E5r utf\u00F6ra de ombyggnads- och \u00E4ndrings\u00E5tg\u00E4rder som specificeras i byggbeskrivningen ovan avseende:" }), _jsxs("p", { className: "text-[10.5px] leading-relaxed", children: [_jsx(Checkbox, { checked: !!values.consent_common_areas }), "Fastighetens gemensamma delar"] }), _jsxs("p", { className: "text-[10.5px] leading-relaxed", children: [_jsx(Checkbox, { checked: !!values.consent_apartment }), "L\u00E4genheten"] }), _jsx("p", { className: "mt-3 text-[10.5px] leading-relaxed", children: "Hyresg\u00E4sten f\u00F6rbinder sig att l\u00E4mna hyresv\u00E4rden tilltr\u00E4de till l\u00E4genheten under den tidsperiod och vid de tillf\u00E4llen som kr\u00E4vs f\u00F6r arbetenas utf\u00F6rande. Hyresg\u00E4sten ansvarar f\u00F6r att l\u00E4genheten \u00E4r t\u00F6md inf\u00F6r renoveringsarbetet. Hyresv\u00E4rden ansvarar inte f\u00F6r st\u00E4dning av l\u00E4genheten eller eventuellt kvarl\u00E4mnat bohag i l\u00E4genheten." })] }), _jsx(KeepTogether, { children: _jsxs(Section, { sectionRef: "underskrift", title: "Underskrift", children: [_jsxs("p", { className: "mt-1 mb-3 text-[10.5px] leading-relaxed", children: [_jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.signing_city) }), ' ', "den", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.signing_date) })] }), _jsx(SigningBlock, { parties: [
                                { label: 'Hyresgäst 1', role: 'lessee', ordinal: 0 },
                                { label: 'Eventuell hyresgäst 2', role: 'lessee', ordinal: 1 },
                            ], signatories: signatories })] }) }), cond(mode, !!values.annex_evakueringsavtal) && (_jsx("p", { className: "mt-4 text-[10.5px] italic text-stone-600", children: "Valfri bilaga: Evakueringsavtal" }))] }));
}
export function Component(props) {
    return (_jsx(TemplateDocument, { header: _jsx(Letterhead, {}), footer: _jsx(PageFooter, {}), children: _jsx(Body, { ...props }) }));
}
//# sourceMappingURL=index.js.map